import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

// Service picker. Accounting rows have no per-item fee (fee is set once for the whole section).
export function ServiceGrid({ services, selected, onToggle, onUpdate, showFee, isAdmin, category, onCatalogueChange }) {
  return (
    <div>
      <div className={`grid gap-x-5 ${showFee ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`} data-testid={`service-grid-${category}`}>
        {services.map((svc) => (
          <ServiceRow
            key={svc.key}
            svc={svc}
            picked={selected.find((s) => s.key === svc.key)}
            onToggle={onToggle}
            onUpdate={onUpdate}
            showFee={showFee}
            isAdmin={isAdmin}
            onCatalogueChange={onCatalogueChange}
          />
        ))}
      </div>
      {isAdmin && <AddServiceRow category={category} onAdded={onCatalogueChange} />}
    </div>
  );
}

function ServiceRow({ svc, picked, onToggle, onUpdate, showFee, isAdmin, onCatalogueChange }) {
  const on = !!picked;
  async function remove() {
    if (!window.confirm(`Remove "${svc.label}" from the service list? Existing onboardings are not affected.`)) return;
    try {
      await api.delete(`/onboarding/services/${svc.key}`);
      toast.success("Service removed");
      onCatalogueChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  }
  return (
    <div className={`group py-1 px-1 flex items-center gap-2 border-b border-[var(--line)] min-h-[32px] transition-colors ${on ? "bg-[var(--navy)]/[0.03]" : ""}`}>
      <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
        <input data-testid={`service-${svc.key}`} type="checkbox" checked={on} onChange={(e) => onToggle(svc, e.target.checked)} />
        <span className={`text-[13px] truncate ${on ? "font-medium" : ""}`} title={svc.label}>{svc.label}</span>
      </label>
      {on && svc.periods && (
        <select
          data-testid={`service-cycle-${svc.key}`}
          value={picked.period || svc.periods[0]}
          onChange={(e) => onUpdate(svc.key, { period: e.target.value })}
          className="input text-[11px] !py-0.5 !pl-1.5 !pr-6 !w-[104px] !min-h-0 shrink-0"
          title="Cycle"
        >
          {svc.periods.map(p => <option key={p} value={p}>{p.replace("_", " ")}</option>)}
        </select>
      )}
      {on && showFee && <FeeInputs svc={svc} picked={picked} onUpdate={onUpdate} />}
      {isAdmin && (
        <button data-testid={`service-remove-${svc.key}`} onClick={remove} title="Remove from list" className="opacity-0 group-hover:opacity-100 transition-opacity text-[var(--ink-2)] hover:text-[var(--err)]">
          <X size={12} />
        </button>
      )}
    </div>
  );
}

function FeeInputs({ svc, picked, onUpdate }) {
  return (
    <>
      <div className="flex items-center gap-1">
        <span className="text-xs text-[var(--ink-2)]">£</span>
        <input
          data-testid={`service-fee-${svc.key}`}
          type="number" min="0" step="0.01"
          value={picked.fee || ""}
          onChange={(e) => onUpdate(svc.key, { fee: e.target.value === "" ? 0 : Number(e.target.value) })}
          placeholder="0.00"
          className="input text-xs !w-20 text-right !py-0.5 !min-h-0"
        />
      </div>
      <select
        data-testid={`service-period-${svc.key}`}
        value={picked.fee_period}
        onChange={(e) => onUpdate(svc.key, { fee_period: e.target.value })}
        className="input text-[11px] !py-0.5 !px-1 !w-20 !min-h-0"
        title="Billing"
      >
        <option value="annual">/ year</option>
        <option value="monthly">/ month</option>
        <option value="one_off">one-off</option>
      </select>
    </>
  );
}

function AddServiceRow({ category, onAdded }) {
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  async function add() {
    if (!label.trim()) return;
    setBusy(true);
    try {
      await api.post("/onboarding/services", { label: label.trim(), category });
      toast.success(`Added "${label.trim()}"`);
      setLabel("");
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add service");
    } finally { setBusy(false); }
  }
  return (
    <div className="flex items-center gap-2 pt-2 mt-1 border-t border-dashed border-[var(--line)]">
      <input
        data-testid={`add-service-input-${category}`}
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") add(); }}
        placeholder="New service name (admin)"
        className="input-slim text-xs flex-1 min-w-0 max-w-xs"
      />
      <button data-testid={`add-service-btn-${category}`} onClick={add} disabled={busy || !label.trim()} className="btn-ghost flex items-center gap-1 text-xs !py-1 !px-2 shrink-0 whitespace-nowrap">
        <Plus size={11} /> Add service
      </button>
    </div>
  );
}
